import flatMap from 'lodash/flatMap';
import memoize from 'lodash/memoize';

import { Category, Item, Option, OptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';

// region Copied from src/modules/menu/
// All the functions below are copied from src/modules/menu/utils.ts and src/modules/menu/items/utils.ts etc.
// in order to not introduce cross-dependencies between modules.

export type OptionBindingPurchasabilityValidationError =
  | 'all_option_values_disabled'
  | 'too_many_disabled_option_values';

// This ID is hard-coded in restaurant-api. Every menu automatically has this
// category. We use this in the client to hide features from regular items that
// are meant to be used with specials only.
export const WOLT_SPECIALS_CATEGORY_ID = '000000000000000000000002';

const flattenCategories = (categories: Category[]) =>
  categories.flatMap(category => [category, ...category.child_categories]);

export const buildCategoryByItemIdIndex = memoize((categories: Category[]) => {
  const categoryByItemId: { [itemId: string]: Category } = {};
  for (const category of flattenCategories(categories)) {
    for (const itemBinding of category.item_bindings) {
      categoryByItemId[itemBinding.item_id] = category;
    }
  }
  return categoryByItemId;
});
// Use WeakMap to avoid leaking memory. Overriding the cache of a memoized function instance is not documented, but it
// works with current Lodash memoize implementation. It is possible that in future this override breaks and memoize will
// resort to default cache implementation which leaks memory.
buildCategoryByItemIdIndex.cache = new WeakMap();

export const getItemCategory = (categories: Category[], itemId: string): Category | undefined => {
  const index = buildCategoryByItemIdIndex(categories);
  return index[itemId];
};

const optionValueBelongsToOptions = (options: Option[], optionValueId: string) => {
  return options.some(o => o.values.some(ov => ov.id === optionValueId));
};

const isOptionValueEnabled = (options: Option[], optionValueId: string) => {
  const allOptionValues = flatMap(options, option => option.values);
  const prerequisiteValue = allOptionValues.find(optionValue => optionValue.id === optionValueId);
  if (!prerequisiteValue) {
    throw new Error(`Option value with ID ${optionValueId} not found`);
  }
  return prerequisiteValue.enabled;
};

const optionBindingCanBeVisible = (optionsBoundToItem: Option[], optionBinding: OptionBinding) => {
  return optionBinding.prerequisite_values.every(prerequisiteVelueId => {
    return (
      optionValueBelongsToOptions(optionsBoundToItem, prerequisiteVelueId) &&
      isOptionValueEnabled(optionsBoundToItem, prerequisiteVelueId)
    );
  });
};

const validateOptionBinding = (
  optionsBoundToItem: Option[],
  optionBinding: OptionBinding,
): OptionBindingPurchasabilityValidationError[] => {
  if (!optionBindingCanBeVisible(optionsBoundToItem, optionBinding)) {
    return [];
  }

  const option = optionsBoundToItem.find(o => o.id === optionBinding.option_id);
  if (!option) {
    throw new Error(`Option with ID ${optionBinding.option_id} not found`);
  }

  if (option.type === 'multi_choice') {
    const multiChoiceConfig = optionBinding.multi_choice_config;
    if (!multiChoiceConfig) {
      throw new Error(`Multi-choice option binding with ID ${optionBinding.id} has no multi_choice_config`);
    }

    const numAvailableSelections = option.values.reduce((acc, optionValue) => {
      if (!optionValue.multi_choice_config) {
        throw new Error(`Multi-choice option value with ID ${optionValue.id} has no multi_choice_config`);
      }

      return optionValue.enabled
        ? acc + (optionValue.multi_choice_config.selection_range.max || multiChoiceConfig.max_single_selections)
        : acc;
    }, 0);
    return numAvailableSelections >= multiChoiceConfig.total_selections.min ? [] : ['too_many_disabled_option_values'];
  }

  return option.values.some(value => value.enabled) ? [] : ['all_option_values_disabled'];
};

export const memoizeWeak = <T extends {}, R>(fn: (obj: T) => R) => {
  const cache = new WeakMap<T, R>();
  return (obj: T) => {
    const cached = cache.get(obj);
    if (cached) {
      return cached;
    }
    const value = fn(obj);
    cache.set(obj, value);
    return value;
  };
};

const buildOptionIndexById = memoizeWeak((options: Option[]) => {
  const index: { [key: string]: Option } = {};
  for (let i = 0, l = options.length; i < l; i++) {
    const option = options[i];
    index[option.id] = option;
  }
  return index;
});

export const validateItemPurchasability = (
  options: Option[],
  item: Item,
): { optionBindingId: string; error: OptionBindingPurchasabilityValidationError }[] => {
  const optionIndex = buildOptionIndexById(options);
  const optionsBoundToItem = item.option_bindings.map(ob => {
    const option: Option | undefined = optionIndex[ob.option_id];
    if (!option) {
      throw new Error(`Option with ID ${ob.option_id} not found`);
    }
    return option;
  });
  return flatMap(item.option_bindings, optionBinding => {
    return validateOptionBinding(optionsBoundToItem, optionBinding).map(validationError => ({
      optionBindingId: optionBinding.id,
      error: validationError,
    }));
  });
};

// endregion
